/**
 * night_composite — the ground sky's background node: the atmosphere + the night (natural + artificial sky, Milky Way,
 * stars, the living sky) + the moon (the night-sky realism wave, 2026-10-06). ONE builder, so the game (SkyModule) and
 * the sky bed (night_sky_probe.html → src/probe/night_sky_bed.ts) draw the identical graph.
 *
 * One `Fn`, so every per-pixel value the branches share is PLACED at its top (THE LAZY-SAMPLE LAW): the atmosphere, the
 * view's screen derivatives (the star core's pixel axes — THE LAZY-VAR LAW: a derivative is taken before any per-pixel
 * branch), the moonlit sky (it raises the stars' limiting magnitude).
 *
 * NO-CYCLE RULE: parameters in, node out; this file imports nothing from SkyModule.
 */
import { vec3, Fn, If, dFdx, dFdy, dot, max, sqrt } from 'three/tsl';
import type { Texture } from 'three/webgpu';
import { buildNightLive, airmassWeight, type NightLiveUniforms } from './night_live.js';
import { buildMoonSky, buildMoonDisc, type MoonLayerUniforms } from './moon_layer.js';
import { buildNightSkyBase, type NightSkyUniforms } from './night_sky.js';
import { buildStarField, type StarFieldInput } from './star_field.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

export interface NightCompositeUniforms {
  /** 0 = full day, 1 = full night (the whole night layer's gate). */
  nightFactor: N;
  /** 1 = clear, 0 = a raining deck hides the night (and the moon). */
  nightClear: N;
  sky: NightSkyUniforms;
  /** Point-star gain lever (1 = the model) and the emissive cap in sky units. */
  starGain: N;
  starCap: N;
  moon: MoonLayerUniforms;
  live: NightLiveUniforms;
}

/** atmosphere + night × nightFactor × nightClear + moon × nightClear, along `viewDir` (scene units). */
export function buildNightComposite(viewDir: N, atmosphereNode: N, bgTex: Texture | null, stars: StarFieldInput | null, u: NightCompositeUniforms): N {
  return Fn(() => {
    const atmo = vec3(atmosphereNode).toVar('skyAtmo');
    const dx = dFdx(viewDir).toVar('skyDx');
    const dy = dFdy(viewDir).toVar('skyDy');
    const pxSize = sqrt(max(dot(dx, dx), dot(dy, dy))).toVar('skyPx');
    const moonSky = buildMoonSky(viewDir, u.moon).toVar('skyMoonSky');
    const moonDisc = buildMoonDisc(viewDir, u.moon);
    const out = vec3(0).toVar('skyNightOut');
    // PERF (shader sweep C, 2026-10-06): by day the night gain is EXACTLY 0 (nightFactor is 0 above a 2.3° sun) — the
    // branch is on a UNIFORM, so it is uniform control flow (the photo's implicit-LOD sample stays legal) and the star
    // loop, the photo, the domes and the living sky never run by day.
    const nightGain = u.nightFactor.mul(u.nightClear);
    If(nightGain.greaterThan(0), () => {
      const base = buildNightSkyBase(viewDir, atmo, moonSky, bgTex, u.sky, pxSize);
      const starU = vec3(0).toVar('skyStars');
      // The star loop runs only above dir.y = −0.02 (night_live fades the stars to exactly 0 there — the half of a night
      // frame the ground covers).
      if (stars) {
        If((viewDir as { y: N }).y.greaterThan(-0.02), () => {
          starU.assign(buildStarField(viewDir, stars, {
            airW: airmassWeight(viewDir), gain: u.starGain, dx, dy,
            bgDisp: base.bgDisp, nelm: base.nelm, extMag: base.extMag, extTint: base.extTint, capUnits: u.starCap,
          }));
        });
      }
      const night = buildNightLive(viewDir, base.rgb.mul(u.sky.unitScene), starU.mul(u.sky.unitScene), u.live);
      out.assign(night.mul(nightGain));
    });
    // THE MOON (2026-09-27): the disc + the moonlit sky, physical — visible by day as the pale daytime moon.
    return atmo.add(out).add(moonDisc.add(moonSky).mul(u.nightClear));
  })();
}
